import SelectableTile from '@carvana/ds-selectable-tile';
import cx from 'classnames';
import { useState } from 'react';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import IncludedBadge from '@/new-cars/IncludedBadge';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeImage from '~/utilities/optimizeImage';
import {
  ContentRow,
  ImageContainer,
  ImageUnavailable,
  OptionName,
  PriceAdd,
  StockCount
} from './OptionSlideCard.styles';

export type OwnProps = {
  count: number;
  disabled?: boolean;
  imageContain?: boolean;
  imageUrl?: string;
  isGreyedOut?: boolean;
  isIncluded?: boolean;
  isSelected: boolean;
  name: string;
  onSelect: () => void;
  priceAdd: number;
  section: string;
};

function formatPriceAdd(priceAdd: number): string {
  return `+$${priceAdd.toLocaleString()}`;
}

export default function OptionSlideCard({
  count,
  disabled = false,
  imageContain = false,
  imageUrl,
  isGreyedOut = false,
  isIncluded = false,
  isSelected,
  name,
  onSelect,
  priceAdd,
  section
}: OwnProps) {
  const [isImageBroken, setIsImageBroken] = useState(false);
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = imageUrl
    ? optimizeImage(imageUrl, { format: 'auto', height: 160, optimize: 'high' })
    : '';

  return (
    <SelectableTile
      className={cx(
        tileStyles.tile,
        'flex h-full w-[198px] flex-col sm:w-full',
        {
          'opacity-50': isGreyedOut,
          'pointer-events-none': isIncluded
        }
      )}
      data-analytics-attributes={JSON.stringify({
        level: 'Options',
        section,
        text: name,
        tileAction: isSelected ? 'deselected' : 'selected'
      })}
      data-analytics-id="New Cars"
      data-testid={`configurator-option-${name}`}
      {...(isIncluded && { tabIndex: -1 })}
      onClick={() => {
        if (!isIncluded) onSelect();
      }}
      selected={isSelected}
      value={name}
    >
      <ImageContainer
        className={
          imageContain || isFallback ? 'bg-background-default' : undefined
        }
        disabled={disabled}
      >
        {optimizedImageUrl && !isImageBroken ? (
          <img
            alt={name}
            className={cx(
              'h-full w-full',
              imageContain || isFallback ? 'object-contain' : 'object-cover'
            )}
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <ContentRow>
        <OptionName disabled={disabled}>{name}</OptionName>
        <PriceAdd disabled={disabled}>{formatPriceAdd(priceAdd)}</PriceAdd>
      </ContentRow>
      {isIncluded ? (
        <IncludedBadge />
      ) : (
        <StockCount className={count === 0 ? 'invisible' : undefined}>
          {count} in stock
        </StockCount>
      )}
    </SelectableTile>
  );
}
